import type { ConnectionStatus } from "@tutor/live";
import type { SessionMode } from "@tutor/protocol";
import { lazy, Suspense, useState } from "react";
import { useT } from "../i18n";
import type { VoiceAccess } from "./access";
// Type only: the module itself is loaded with `import()` below.
import type * as VoicePanelModule from "./VoicePanel";

// The voice entry point on the lesson canvas. Voice mode (the controller, LiveKit and the
// push-to-talk button) is its own chunk: it loads when the child first reaches for the
// button (hover, focus or tap) and the controller is created on the tap.

let loading: Promise<typeof VoicePanelModule> | null = null;

function preload(): Promise<typeof VoicePanelModule> {
  loading ??= import("./VoicePanel");
  return loading;
}

const VoicePanel = lazy(() => preload().then((module) => ({ default: module.VoicePanel })));

export function VoiceDock(props: {
  access: VoiceAccess;
  /** From the live channel (`session.state`): `voice` while the voice agent is in. */
  mode: SessionMode;
  /** The live connection; its banner already tells the child, so voice only disables. */
  status: ConnectionStatus;
  onUnauthorized: () => void;
}) {
  const { access, mode, status, onUnauthorized } = props;
  const t = useT();
  const [open, setOpen] = useState(false);
  const reachable = status === "live" || status === "connecting";

  return (
    <div className="voice-dock" role="region" aria-label={t("voice.label")}>
      {open ? (
        <Suspense
          fallback={
            <p className="voice-status" role="status">
              {t("voice.connecting")}
            </p>
          }
        >
          <VoicePanel
            access={access}
            onClose={() => {
              setOpen(false);
            }}
            onUnauthorized={onUnauthorized}
          />
        </Suspense>
      ) : (
        <button
          type="button"
          className="button voice-open"
          disabled={!reachable}
          onPointerEnter={() => void preload()}
          onFocus={() => void preload()}
          onClick={() => {
            setOpen(true);
          }}
        >
          <MicIcon />
          {/* The session is still in voice (another tab, or a reload): pick it up. */}
          {mode === "voice" ? t("voice.resume") : t("voice.open")}
        </button>
      )}
    </div>
  );
}

function MicIcon() {
  return (
    <svg
      className="voice-icon"
      viewBox="0 0 24 24"
      width="22"
      height="22"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
      <path
        d="M6 11a6 6 0 0 0 12 0M12 17v4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
